import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { EmptyList, LoadingList } from "@/organism";
import { SuperChatContentDto } from "@/dto/SuperChatContentDto";

/**
 * QNA 컨텐츠 목록 오가니즘 Props
 */
export type ListProps = {
  /**
   * QNA 컨텐츠 목록
   */
  list: SuperChatContentDto[];
  /**
   * 로딩 여부
   */
  isLoading?: boolean;
  /**
   * 상세보기 클릭 이벤트
   */
  onEdit: (contentId: number) => void;
};

/**
 * QNA 컨텐츠 목록 오가니즘
 *
 * @description 도장 목록 오가니즘을 참고하여 구현
 * @param props ListProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const List = (props: ListProps) => {
  const { list, isLoading = false, onEdit } = props;

  /**
   * 사용 여부에 따른 칩 색상 반환
   */
  const getUseYnColor = (useYn: string) => {
    switch (useYn) {
      case "Y":
        return "success";
      case "N":
        return "default";
      default:
        return "default";
    }
  };

  /**
   * 사용 여부 텍스트 반환
   */
  const getUseYnText = (useYn: string) => {
    switch (useYn) {
      case "Y":
        return "사용";
      case "N":
        return "미사용";
      default:
        return useYn;
    }
  };

  /**
   * 컨텐츠 렌더링
   */
  const renderContent = () => {
    if (isLoading) {
      return <LoadingList />;
    }

    if (list.length === 0) {
      return <EmptyList />;
    }

    return list.map((content: SuperChatContentDto) => (
      <TableRow key={content.contentId}>
        <TableCell>{content.contentId}</TableCell>
        <TableCell>
          <Chip
            label={content.category || "일반"}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 300,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {content.content}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {content.keywords || "-"}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={getUseYnText(content.useYn)}
            color={getUseYnColor(content.useYn) as any}
            size="small"
          />
        </TableCell>
        <TableCell>
          {content.createdAt
            ? new Date(content.createdAt).toLocaleDateString()
            : "-"}
        </TableCell>
        <TableCell>
          <Tooltip title="상세보기">
            <IconButton size="small" onClick={() => onEdit(content.contentId)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>카테고리</TableCell>
            <TableCell>컨텐츠</TableCell>
            <TableCell>키워드</TableCell>
            <TableCell>사용여부</TableCell>
            <TableCell>생성일</TableCell>
            <TableCell>작업</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </TableContainer>
  );
};
